import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../../../api/axios";
import type {
  TAttribute,
  TCreateAttributeRequest,
  TUpdateAttributeRequest,
  TApiResponse,
  TApiSingleResponse,
} from "../schemas";

const API_BASE_URL = "/menu-attributes";
const QUERY_KEY = "menu-attributes";

// Get all attributes for a restaurant
export const useGetAttributes = (restaurantId: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, restaurantId],
    queryFn: async () => {
      const response = await apiClient.get<TApiResponse<TAttribute>>(
        API_BASE_URL,
        {
          headers: {
            "Restaurant-ID": restaurantId,
          },
        }
      );
      return response.data?.data?.rows || [];
    },
    enabled: !!restaurantId,
  });
};

// Get single attribute by ID
export const useGetAttribute = (attributeId: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, attributeId],
    queryFn: async () => {
      const response = await apiClient.get<TApiSingleResponse<TAttribute>>(
        `${API_BASE_URL}/${attributeId}`
      );
      return response.data.data;
    },
    enabled: !!attributeId,
  });
};

// Create new attribute
export const useCreateAttribute = (restaurantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TCreateAttributeRequest) => {
      const response = await apiClient.post<TApiSingleResponse<TAttribute>>(
        API_BASE_URL,
        data,
        {
          headers: {
            "Restaurant-ID": restaurantId,
          },
        }
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, restaurantId] });
    },
  });
};

// Update existing attribute
export const useUpdateAttribute = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      attributeId,
      data,
    }: {
      attributeId: string;
      data: TUpdateAttributeRequest;
    }) => {
      const response = await apiClient.put<TApiSingleResponse<TAttribute>>(
        `${API_BASE_URL}/${attributeId}`,
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

// Delete attribute
export const useDeleteAttribute = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (attributeId: string) => {
      const response = await apiClient.delete(
        `${API_BASE_URL}/${attributeId}`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

// Upload image type
type TUploadImageResponse = {
  success: boolean;
  data?: {
    fileId: string;
    location: string;
  };
  msg?: string;
};

// Upload image for icon or image field
export const useUploadImage = () => {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("image", file);
      const response = await apiClient.post<TUploadImageResponse>(
        "/files/upload-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    },
  });
};
